import { MapPin, Phone, Mail, ArrowUp } from "lucide-react";

const footerLinks = {
  products: ["Coconut Fiber", "Cocopeat Blocks", "Coir Pith", "Coir Rope", "Geotextiles"],
  company: ["About Us", "Our Process", "Quality Control", "Sustainability"],
  support: ["Contact Us", "FAQs", "Shipping Info", "Payment Terms"],
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_coirbiz/artifacts/iziklf0y_Logo.png";

export default function Footer({ onNavigate }) {
  return (
    <footer className="bg-[#0F172A] text-white relative overflow-hidden" data-testid="footer">
      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-coir/50 to-transparent" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-forest/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-coir/5 rounded-full blur-3xl" />
      
      <div className="container-custom py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Sri Srinivasa Coir Industry Logo" 
                className="w-16 h-16 object-contain bg-white rounded-lg p-1 shadow-lg"
              />
              <div>
                <span className="text-xl font-bold font-serif block leading-tight">Sri Srinivasa</span>
                <span className="text-lg font-bold text-forest font-serif">Coir Industry</span>
              </div>
            </div>
            
            <p className="text-slate-400 mb-8 max-w-sm font-sans text-sm leading-relaxed">
              Leading manufacturer of premium coconut fiber and coco peat products.
              Committed to quality, sustainability, and customer satisfaction since 2006.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-forest/20 transition-colors duration-300">
                  <MapPin className="w-5 h-5 text-forest" />
                </div>
                <div>
                  <p className="text-white/80 text-sm font-sans font-medium">Address</p>
                  <p className="text-slate-400 text-sm font-sans">S Rayavaram Mandal, 17/1, Koruprolu, Andhra Pradesh 531083, India</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-forest/20 transition-colors duration-300">
                  <Phone className="w-5 h-5 text-forest" />
                </div>
                <div>
                  <p className="text-white/80 text-sm font-sans font-medium">Phone</p>
                  <p className="text-slate-400 text-sm font-sans">+91 9666195695 / +91 7013696728</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:bg-forest/20 transition-colors duration-300">
                  <Mail className="w-5 h-5 text-forest" />
                </div>
                <div>
                  <p className="text-white/80 text-sm font-sans font-medium">Email</p>
                  <p className="text-slate-400 text-sm font-sans">siddardh3.v@gmail.com</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Links Sections */}
          {[
            ["Products", footerLinks.products], 
            ["Company", footerLinks.company], 
            ["Support", footerLinks.support]
          ].map(([title, links]) => (
            <div key={title}>
              <h4 className="text-lg font-serif mb-6 tracking-tight relative inline-block">
                {title}
                <div className="absolute -bottom-2 left-0 w-8 h-0.5 bg-gradient-to-r from-forest to-coir" />
              </h4>
              <ul className="space-y-3">
                {links.map((link, i) => (
                  <li key={i}>
                    <button className="text-slate-400 hover:text-forest transition-colors duration-300 text-sm font-sans text-left link-underline">
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-500 text-sm text-center md:text-left font-sans">
              <p>&copy; {new Date().getFullYear()} Sri Srinivasa Coir Industry. All rights reserved.</p>
              <p className="mt-1 text-xs text-slate-600">
                Premium Coconut Fiber & Coco Peat Products · Made in India 🇮🇳
              </p>
            </div>
            
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} 
              className="w-12 h-12 bg-gradient-to-br from-forest to-emerald-600 flex items-center justify-center hover:shadow-lg hover:shadow-forest/30 transition-all duration-300 hover:-translate-y-1 group" 
              data-testid="back-to-top-btn"
              aria-label="Back to top"
            >
              <ArrowUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
